import { formatArPhoneDisplay } from '@shared/phone';
import { buildCsvFromRows, escapeCsvCell } from '../../shared/csv-core/infra/buildCsv';
import { formatCustomerCreditLimitLabel } from './customerCreditLimit';
import { getCustomerMovementDisplayLabel } from '../domain/customerMovementLabels';
import type { CustomerAccountRow, CustomerMovementRow } from '../domain/customerTypes';

export type CustomerStatementParams = {
  customer: CustomerAccountRow;
  rows: CustomerMovementRow[];
  actorLabel: (userId: string | null | undefined) => string;
  formatMoney: (n: number) => string;
  fromDate?: string;
  toDate?: string;
};

function formatIsoDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('es-AR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return iso;
  }
}

export function buildCustomerStatementCsv({
  customer,
  rows,
  actorLabel,
  formatMoney,
  fromDate,
  toDate,
}: CustomerStatementParams): string {
  const lines: string[] = [];

  lines.push('ESTADO DE CUENTA');
  lines.push('');
  lines.push(`Cliente,${escapeCsvCell(customer.name)}`);
  if (customer.phone) {
    lines.push(`Teléfono,${escapeCsvCell(formatArPhoneDisplay(customer.phone))}`);
  }
  if (customer.cuit) lines.push(`CUIT,${escapeCsvCell(customer.cuit)}`);
  if (customer.email) lines.push(`Email,${escapeCsvCell(customer.email)}`);
  if (customer.address) lines.push(`Dirección,${escapeCsvCell(customer.address)}`);
  lines.push(`Límite de crédito,${escapeCsvCell(formatCustomerCreditLimitLabel(customer.creditLimit, formatMoney))}`);
  lines.push(`Saldo actual,${escapeCsvCell(formatMoney(customer.balance))}`);
  if (customer.paymentTermsDays && customer.paymentTermsDays > 0) {
    lines.push(`Días de pago,${customer.paymentTermsDays}`);
  }
  if (fromDate || toDate) {
    const periodo = [fromDate, toDate].filter(Boolean).join(' al ');
    lines.push(`Período,${escapeCsvCell(periodo)}`);
  }
  lines.push(`Generado,${escapeCsvCell(formatIsoDate(new Date().toISOString()))}`);
  lines.push('');

  if (rows.length === 0) {
    lines.push('Sin movimientos en el período seleccionado.');
    return lines.join('\n');
  }

  const totalCharges = rows
    .filter((r) => r.type === 'charge')
    .reduce((s, r) => s + r.amount, 0);
  const totalPayments = rows
    .filter((r) => r.type === 'payment')
    .reduce((s, r) => s + r.amount, 0);

  const header = [
    'fecha',
    'tipo',
    'monto',
    'nota',
    'venta_id',
    'operador',
    'id_movimiento',
  ];

  const dataLines = rows.map((row) => [
    escapeCsvCell(formatIsoDate(row.createdAt)),
    escapeCsvCell(getCustomerMovementDisplayLabel(row)),
    escapeCsvCell(formatMoney(row.amount)),
    escapeCsvCell(row.note ?? ''),
    escapeCsvCell(row.saleShortId ?? row.saleId ?? ''),
    escapeCsvCell(actorLabel(row.performedByUserId)),
    escapeCsvCell(row.id),
  ]);

  lines.push(buildCsvFromRows(header, dataLines));
  lines.push('');
  lines.push(`Total consumos,${escapeCsvCell(formatMoney(totalCharges))}`);
  lines.push(`Total pagos,${escapeCsvCell(formatMoney(totalPayments))}`);
  lines.push(`Saldo neto del período,${escapeCsvCell(formatMoney(totalCharges - totalPayments))}`);

  return lines.join('\n');
}
